// Imperative NL planner hook. Wraps the backend planner contract,
// translates the raw response into the UI-facing discriminated union,
// and appends user + assistant messages to the chat store.
//
// Multi-turn: `lastFilter` from the store is passed back as
// `prior_filter_group` so the planner refines/broadens/replaces according
// to planner_system.md §Refinement turns.
import { useCallback } from "react";
import { ApiError, planQuery } from "../lib/api";
import { useAnaMoniStore } from "../store/anaMoniStore";
import type {
  AnaMoniResponse,
  AnamoniBackendResponse,
  ClarificationChip,
  FilterGroup,
} from "../types/inpact";

function interpret(
  resp: AnamoniBackendResponse,
  query: string,
): AnaMoniResponse {
  const explanation = {
    interpretation: resp.interpretation,
    appliedFilterGroup: resp.applied_filter_group,
    snapshot: resp.snapshot,
    limitations: resp.limitations,
  };
  if (resp.kind === "out_of_domain") {
    return { kind: "out_of_domain", confidence: resp.confidence, ...explanation };
  }

  if (resp.kind === "refusal") {
    return { kind: "refusal", confidence: resp.confidence, ...explanation };
  }

  if (resp.kind === "ambiguity") {
    return {
      kind: "ambiguity",
      question: resp.clarification_question ?? "Could you rephrase that?",
      chips: buildClarificationChips(resp, query),
      confidence: resp.confidence,
      rawFilter: resp.filter_group as FilterGroup,
      rawQuery: query,
      ...explanation,
    };
  }

  if (resp.kind === "partial") {
    const blockedByPolicy = resp.limitations.some(
      (limitation) => limitation.code === "field_forbidden",
    );
    return {
      kind: "partial",
      filter: resp.filter_group as FilterGroup,
      confidence: resp.confidence,
      autoExecute: false,
      question: resp.clarification_question,
      chips: buildClarificationChips(resp, query),
      rawQuery: query,
      canApply: resp.ambiguities.length === 0 && !blockedByPolicy,
      ...explanation,
    };
  }

  if (resp.kind === "aggregation" && resp.aggregation) {
    return {
      kind: "aggregation",
      filter: resp.filter_group as FilterGroup,
      result: resp.aggregation,
      confidence: resp.confidence,
      autoExecute: resp.auto_execute,
      ...explanation,
    };
  }

  return {
    kind: "success",
    filter: resp.filter_group as FilterGroup,
    confidence: resp.confidence,
    autoExecute: resp.auto_execute,
    ...explanation,
  };
}

function buildClarificationChips(
  resp: AnamoniBackendResponse,
  query: string,
): ClarificationChip[] {
  const out: ClarificationChip[] = [];
  for (const a of resp.ambiguities) {
    for (const opt of a.options) {
      if (out.length >= 4) break;
      out.push({
        refinedQuery: `${query} (${a.term} → ${opt})`,
        label: `${a.term}: ${opt}`,
      });
    }
    if (out.length >= 4) break;
  }
  return out;
}

function summariseAssistant(resp: AnaMoniResponse): string {
  switch (resp.kind) {
    case "success":
      return resp.autoExecute
        ? `Filter applied (${resp.filter.rules.length} rule${resp.filter.rules.length === 1 ? "" : "s"}).`
        : `Filter ready for confirmation (${resp.filter.rules.length} rule${resp.filter.rules.length === 1 ? "" : "s"}).`;
    case "ambiguity":
      return resp.question;
    case "out_of_domain":
      return "I can't search that — INPACT doesn't track that data.";
    case "refusal":
      return "The proposed filter could not be safely applied.";
    case "aggregation":
      return resp.autoExecute
        ? `Breakdown by ${resp.result.chart_spec.group_by} (${resp.result.total} contacts across ${resp.result.rows.length} groups).`
        : `Breakdown by ${resp.result.chart_spec.group_by} ready for confirmation (${resp.result.total} contacts across ${resp.result.rows.length} groups).`;
    case "partial":
      return resp.question
        ?? "A filter is ready for review, but the requested result was not computed.";
  }
}

function safePlannerError(error: unknown): string {
  if (
    error instanceof ApiError
    && (error.code === "field_forbidden" || error.code === "object_unavailable")
  ) {
    return error.detail;
  }
  return "AnaMoni is temporarily unavailable. Please try again.";
}

export function useAnaMoni() {
  const { appendUser, appendAssistant, setPending, lastFilter, setLastFilter } =
    useAnaMoniStore();

  const submit = useCallback(
    async (query: string): Promise<AnaMoniResponse | null> => {
      const trimmed = query.trim();
      if (!trimmed) return null;
      appendUser(trimmed);
      setPending(true);
      try {
        const raw = await planQuery(trimmed, lastFilter ?? undefined);
        const interpreted = interpret(raw, trimmed);
        appendAssistant(interpreted, summariseAssistant(interpreted));
        if (
          (interpreted.kind === "success" || interpreted.kind === "aggregation")
          && interpreted.autoExecute
        ) {
          setLastFilter(interpreted.filter);
        }
        return interpreted;
      } catch (err) {
        const msg = safePlannerError(err);
        appendAssistant({ kind: "error", message: msg }, msg);
        return null;
      } finally {
        setPending(false);
      }
    },
    [appendUser, appendAssistant, setPending, lastFilter, setLastFilter],
  );

  return { submit };
}
